import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import {
  getAuth,
  Auth,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from "firebase/auth";
import { IAuthAdapter, AuthUser } from "~/core/domain/services/i-auth-adapter";
import { BackendUserRepository } from "~/core/adapters/backend/backend-user-repository";

/**
 * Firebase認証アダプターの実装
 */
export class FirebaseAuthAdapter implements IAuthAdapter {
  private firebaseApp: FirebaseApp;
  private auth: Auth;
  private tokenRefreshUnsubscribe: (() => void) | null = null;
  private userRepository: BackendUserRepository;

  constructor() {
    // Firebaseアプリが既に初期化されている場合はそれを使用
    if (getApps().length === 0) {
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string,
        messagingSenderId: import.meta.env
          .VITE_FIREBASE_MESSAGING_SENDER_ID as string,
        appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
      };

      this.firebaseApp = initializeApp(firebaseConfig);
    } else {
      this.firebaseApp = getApps()[0];
    }

    this.auth = getAuth(this.firebaseApp);

    // トークン更新を監視してCookieを自動更新
    this.setupTokenRefreshListener();

    // UserRepositoryを初期化
    this.userRepository = new BackendUserRepository();
  }

  /**
   * JWTトークンの期限をチェック
   * @param token - JWTトークン
   * @param bufferSeconds - 期限切れとみなすバッファ時間（秒）。デフォルトは5分
   * @returns 期限切れまたは期限が近い場合true
   */
  private isTokenExpired(token: string, bufferSeconds: number = 300): boolean {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return true; // 不正なトークンは期限切れとして扱う
      }

      // base64urlデコード
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")),
      );

      const exp = payload.exp;
      if (!exp) {
        return true; // expがない場合は期限切れとして扱う
      }

      // 現在時刻 + バッファ時間と比較
      const expirationTime = exp * 1000;
      const now = Date.now();
      return expirationTime <= now + bufferSeconds * 1000;
    } catch (error) {
      console.error("Failed to check token expiration:", error);
      return true; // エラー時は期限切れとして扱う
    }
  }

  /**
   * Cookieにトークンを保存
   * 公開メソッドとして、呼び出し元で明示的にCookieを設定できるようにする
   */
  public setTokenToCookie(token: string): void {
    document.cookie = `firebase-auth-token=${token}; path=/; max-age=${
      60 * 60 * 24 * 7
    }; SameSite=Lax${import.meta.env.PROD ? "; Secure" : ""}`;
  }

  /**
   * Cookieからトークンを取得
   */
  private getTokenFromCookie(): string | null {
    const cookies = document.cookie;
    const match = cookies.match(/firebase-auth-token=([^;]+)/);
    return match ? match[1] : null;
  }

  /**
   * トークン更新を監視してCookieを自動更新
   */
  private setupTokenRefreshListener(): void {
    // クライアントサイドでのみ動作
    if (typeof window === "undefined") {
      return;
    }

    this.tokenRefreshUnsubscribe = onIdTokenChanged(
      this.auth,
      async (user: FirebaseUser | null) => {
        if (user) {
          try {
            console.log("[AUTH_ADAPTER] onIdTokenChanged: user signed in", { uid: user.uid, email: user.email });
            // 新しいトークンを取得（自動的にリフレッシュされる）
            const newToken = await user.getIdToken();

            // Cookieを更新
            this.setTokenToCookie(newToken);

            console.log("[AUTH_ADAPTER] Token refreshed and cookie updated via onIdTokenChanged", { tokenLength: newToken.length });
          } catch (error) {
            console.error("[AUTH_ADAPTER] Failed to refresh token:", error);
          }
        } else {
          console.log("[AUTH_ADAPTER] onIdTokenChanged: user signed out, clearing cookie");
          // ログアウト時はCookieを削除
          document.cookie =
            "firebase-auth-token=; path=/; max-age=0; SameSite=Lax";
        }
      },
    );
  }

  /**
   * クリーンアップ（必要に応じて）
   */
  public cleanup(): void {
    if (this.tokenRefreshUnsubscribe) {
      this.tokenRefreshUnsubscribe();
      this.tokenRefreshUnsubscribe = null;
    }
  }

  /**
   * メールアドレスとパスワードでログイン
   * 
   * 注意: Cookieの設定は呼び出し元で行うこと。
   * 認証成功後、呼び出し元で`user.getIdToken()`を呼び出してトークンを取得し、
   * `setTokenToCookie(token)`を呼び出すこと。
   */
  async signInWithEmailAndPassword(
    email: string,
    password: string,
  ): Promise<{ user: AuthUser | null; error: Error | null; firebaseUser?: FirebaseUser }> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        this.auth,
        email,
        password,
      );
      const user = userCredential.user;

      return {
        user: {
          id: user.uid,
          email: user.email ?? null,
        },
        error: null,
        firebaseUser: user, // トークン取得のためにFirebaseUserを返す
      };
    } catch (error) {
      return {
        user: null,
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  /**
   * ログアウト
   */
  async signOut(): Promise<{ error: Error | null }> {
    try {
      await firebaseSignOut(this.auth);

      // Cookieを削除
      document.cookie = "firebase-auth-token=; path=/; max-age=0; SameSite=Lax";

      return { error: null };
    } catch (error) {
      return {
        error: error instanceof Error ? error : new Error(String(error)),
      };
    }
  }

  async getAccessToken(request?: Request): Promise<string | undefined> {
    try {
      // サーバーサイドの場合、Cookieからトークンを取得
      if (request) {
        const cookies = request.headers.get("Cookie") || "";
        const match = cookies.match(/firebase-auth-token=([^;]+)/);
        if (match && match[1]) {
          console.log("[AUTH_ADAPTER] Token retrieved from cookie (server-side)");
          return match[1];
        }
        console.log("[AUTH_ADAPTER] No token found in cookie (server-side)");
        return undefined;
      }

      // クライアントサイドの場合
      const user = this.auth.currentUser;
      console.log("[AUTH_ADAPTER] getAccessToken called", { hasUser: !!user, userId: user?.uid });

      // Cookieからトークンを取得
      const cookieToken = this.getTokenFromCookie();

      if (!user) {
        console.log("[AUTH_ADAPTER] No user, returning cookie token", { hasCookieToken: !!cookieToken });
        // ユーザーが存在しない場合、Cookieから取得したトークンを返す
        // （ただし、期限切れの可能性がある）
        return cookieToken || undefined;
      }

      // Firebase Authからトークンを取得（自動的にリフレッシュされる）
      console.log("[AUTH_ADAPTER] Getting fresh token from Firebase Auth");
      const freshToken = await user.getIdToken();

      // Cookieのトークンと比較して、更新が必要かチェック
      if (
        !cookieToken ||
        this.isTokenExpired(cookieToken) ||
        cookieToken !== freshToken
      ) {
        console.log("[AUTH_ADAPTER] Updating cookie with fresh token", { 
          hadCookieToken: !!cookieToken, 
          tokenExpired: cookieToken ? this.isTokenExpired(cookieToken) : false,
          tokenChanged: cookieToken !== freshToken
        });
        // Cookieを更新
        this.setTokenToCookie(freshToken);
      } else {
        console.log("[AUTH_ADAPTER] Cookie token is up to date");
      }

      return freshToken;
    } catch (error) {
      console.error("[AUTH_ADAPTER] Failed to get access token:", error);
      return undefined;
    }
  }

  async getUser(request?: Request): Promise<AuthUser | null> {
    // クライアントサイドではRequestは不要
    if (request) {
      throw new Error("クライアントサイドではRequestオブジェクトは不要です");
    }

    try {
      let user = this.auth.currentUser;
      console.log("[AUTH_ADAPTER] getUser called", { hasCurrentUser: !!user, uid: user?.uid, email: user?.email });
      
      // Firebase Auth の状態復元を待つ（ページロード直後は currentUser が null の可能性がある）
      if (!user) {
        console.log("[AUTH_ADAPTER] currentUser is null, waiting for auth state restoration...");
        // onAuthStateChanged の最初のコールバックを待つ（最大3秒）
        user = await new Promise<FirebaseUser | null>((resolve) => {
          const timeout = setTimeout(() => {
            unsubscribe();
            console.log("[AUTH_ADAPTER] Auth state restoration timeout, returning null");
            resolve(null);
          }, 3000);
          
          const unsubscribe = onAuthStateChanged(this.auth, (firebaseUser) => {
            clearTimeout(timeout);
            unsubscribe();
            console.log("[AUTH_ADAPTER] Auth state restored", { hasUser: !!firebaseUser, uid: firebaseUser?.uid });
            resolve(firebaseUser);
          });
        });
      }
      
      if (!user) {
        console.log("[AUTH_ADAPTER] No currentUser after waiting, returning null");
        return null;
      }

      // バックエンドからユーザー情報を取得（firebaseUidで検索）
      console.log("[AUTH_ADAPTER] Fetching user from backend", { firebaseUid: user.uid });
      const backendUser = await this.userRepository.getCurrentUser();
      console.log("[AUTH_ADAPTER] Backend user fetched", { userId: backendUser.id, email: backendUser.email });

      return {
        id: backendUser.id, // DBのUUIDを返す
        email: backendUser.email,
      };
    } catch (error) {
      console.error("[AUTH_ADAPTER] getUser error", error);
      const status =
        error && typeof error === "object" && "status" in error
          ? (error as { status?: number }).status
          : undefined;
      if (status && [401, 403, 404].includes(status)) {
        // バックエンドにユーザーが存在しない/認可不可ならログアウトしてループを止める
        await this.signOut();
      }
      console.error("Failed to get user:", error);
      return null;
    }
  }

  /**
   * 認証状態の変更を監視する（オプション）
   */
  onAuthStateChange(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(
      this.auth,
      (firebaseUser: FirebaseUser | null) => {
        if (firebaseUser) {
          callback({
            id: firebaseUser.uid,
            email: firebaseUser.email ?? null,
          });
        } else {
          callback(null);
        }
      },
    );
  }
}
